import { useIntl } from '@leagueplatform/locales';
import { useGetUserProfile } from '@leagueplatform/web-common';

// Shapes the raw `useGetUserProfile` response into the display values the
// Personal Info page renders, keeping the page presentational and making the
// formatting logic unit-testable on its own.
export const usePersonalInfo = () => {
  const { formatDate } = useIntl();
  const { data, isLoading, isError } = useGetUserProfile();

  const userProfile = data?.userProfile;

  const middleInitial = userProfile?.middleInitial
    ? `${userProfile.middleInitial}.`
    : undefined;

  const fullName = [
    userProfile?.firstName,
    middleInitial,
    userProfile?.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  const birthDate = userProfile?.dateOfBirth
    ? formatDate(userProfile.dateOfBirth, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'UTC',
      })
    : undefined;

  // Prefer an explicit mailing location, then the default, then the first available.
  const mailingAddress =
    userProfile?.locations?.find((location) => location.type === 'mailing') ??
    userProfile?.locations?.find((location) => location.isDefault) ??
    userProfile?.locations?.[0];

  const streetLine = [mailingAddress?.address1, mailingAddress?.address2]
    .filter(Boolean)
    .join(', ');
  const cityStateLine = [
    [mailingAddress?.city, mailingAddress?.state].filter(Boolean).join(', '),
    mailingAddress?.postal,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    isLoading,
    // The page treats a missing profile the same as an error state.
    isError: isError || !data,
    fullName,
    preferredFirstName: userProfile?.preferredFirstName,
    birthDate,
    streetLine,
    cityStateLine,
  };
};
